import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Scale,
  ArrowRight,
  Info,
  Loader2,
  Package
} from 'lucide-react';

const SmartRoundingPreview = () => {
  const { api, isAuthenticated } = useAuth();
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRounded, setTotalRounded] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cart/smart-rounding-preview');
      setPreview(res.data.preview || []);
      setTotalRounded(res.data.total_items_rounded || 0);
    } catch (err) {
      console.error('Failed to load rounding preview:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Card data-testid="smart-rounding-loading">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="smart-rounding-preview">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-600" />
          <CardTitle className="text-lg">Smart Rounding</CardTitle>
        </div>
        <CardDescription>
          We round up to store pack sizes so you have exactly what you need (and maybe a little extra).
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {preview.length === 0 ? (
          <div className="text-center py-6">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              No rounding needed! All items match store sizes perfectly.
            </p>
          </div>
        ) : (
          <>
            {/* Summary badge */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {totalRounded} item{totalRounded !== 1 ? 's' : ''} rounded
              </Badge>
              <span className="text-sm text-muted-foreground">
                to match store pack sizes
              </span>
            </div>

            {/* Rounding items */}
            <div className="space-y-2">
              {preview.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  data-testid={`rounding-item-${item.ingredient.replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-sm">
                      <p className="font-medium capitalize">{item.ingredient}</p>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>{item.exact_needed} {item.unit}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="text-foreground font-medium">
                          {item.will_purchase} {item.display_unit || item.unit}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="secondary" className="text-xs">
                      +{item.extra} {item.unit} extra
                    </Badge>
                    {item.reason && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {item.reason}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Tip */}
            <p className="text-xs text-muted-foreground text-center">
              Extras can be stored for later or used in other recipes. Check the Leftovers tab for suggestions!
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartRoundingPreview;
